import {assert} from 'Asserts';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartNodeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {
  ProcessFlowchartNodeAdapter,
  ProcessFlowchartTaskSchemaNodeAdapter,
} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {ProcessesPluginBase} from './ProcessesPluginBase';

/**
 * Registry for plugins and the extension points they provide to the Processes platform and editor.
 *
 * This registry is used internally by the Processes editor (see ProcessFlowchartConfigEditor) to manage
 * and retrieve application team extensions to the editor. It should never be used outside of processes
 * editor components. Application teams should never interact with this class directly.
 *
 * Application teams subclass ProcessesPluginBase to specify the extensions they're making to the editor.
 * That base type provides the method registerForEditor() that the application team should call
 * to register its extensions with the editor before the editor is used.
 *
 * The registry should be used as a singleton via the PLUGIN_REGISTRY_INSTANCE symbol (see below).
 */
class PluginRegistry {
  private readonly pluginNameToPlugin: Map<string, ProcessesPluginBase>;
  private readonly flowchartTypeToPlugin: Map<string, ProcessesPluginBase>;
  private readonly executionTypeToTaskSchemaNodeAdapters: Map<
    ProcessExecutionType,
    ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown>
  >;
  private readonly nodeTypeToNodeAdapters: Map<ProcessFlowchartNodeType, ProcessFlowchartNodeAdapter>;

  /**
   * @internal
   */
  constructor() {
    this.executionTypeToTaskSchemaNodeAdapters = new Map();
    this.pluginNameToPlugin = new Map();
    this.flowchartTypeToPlugin = new Map();
    this.nodeTypeToNodeAdapters = new Map();
  }

  /**
   * Register a plugin and all of its extensions with the editor.
   *
   * An error is thrown if a plugin with the same name is already registered, or if any plugin-provided
   * extensions are already registered.
   *
   * @param plugin the plugin with extensions to register.
   */
  registerPluginAndItsExtensions(plugin: ProcessesPluginBase): void {
    if (this.pluginNameToPlugin.has(plugin.pluginName)) {
      throw new Error(`PluginRegistry already has a plugin with name ${plugin.pluginName}`);
    }
    plugin.nodeAdapters.forEach((nodeAdapter) => {
      if (nodeAdapter.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
        assert(
          nodeAdapter instanceof ProcessFlowchartTaskSchemaNodeAdapter,
          'Node adapter must be a task schema node adapter'
        );
        if (this.executionTypeToTaskSchemaNodeAdapters.has(nodeAdapter.executionType)) {
          throw new Error(
            `PluginRegistry already has a node adapter for execution type ${nodeAdapter.executionType}`
          );
        }
      } else if (this.nodeTypeToNodeAdapters.has(nodeAdapter.nodeType)) {
        throw new Error(`PluginRegistry already has a node adapter for node type ${nodeAdapter.nodeType}`);
      }
    });
    this.pluginNameToPlugin.set(plugin.pluginName, plugin);
    if (plugin.flowchartTypeName) {
      this.flowchartTypeToPlugin.set(plugin.flowchartTypeName, plugin);
    }
    plugin.nodeAdapters.forEach((nodeAdapter) => {
      if (nodeAdapter.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
        assert(
          nodeAdapter instanceof ProcessFlowchartTaskSchemaNodeAdapter,
          'Node adapter must be a task schema node adapter'
        );
        this.executionTypeToTaskSchemaNodeAdapters.set(nodeAdapter.executionType, nodeAdapter);
      } else {
        this.nodeTypeToNodeAdapters.set(nodeAdapter.nodeType, nodeAdapter);
      }
    });
  }

  /**
   * Deregister a plugin and all of its extensions from the editor.
   *
   * An error is thrown if the plugin is not already registered.
   *
   * This is meant to be used to clean up after tests - it is not intended to be used as part of production
   * code.
   *
   * @param plugin the plugin with extensions to deregister.
   */
  deregisterPluginAndItsExtensions(plugin: ProcessesPluginBase): void {
    if (!this.pluginNameToPlugin.has(plugin.pluginName)) {
      throw new Error(`PluginRegistry does not have a plugin with name ${plugin.pluginName}`);
    }
    plugin.nodeAdapters.forEach((nodeAdapter) => {
      if (nodeAdapter.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
        assert(
          nodeAdapter instanceof ProcessFlowchartTaskSchemaNodeAdapter,
          'Node adapter must be a task schema node adapter'
        );
        if (!this.executionTypeToTaskSchemaNodeAdapters.has(nodeAdapter.executionType)) {
          throw new Error(
            `PluginRegistry does not have a node adapter for execution type ${nodeAdapter.executionType}`
          );
        }
      } else if (!this.nodeTypeToNodeAdapters.has(nodeAdapter.nodeType)) {
        throw new Error(`PluginRegistry does not have a node adapter for node type ${nodeAdapter.nodeType}`);
      }
    });
    this.pluginNameToPlugin.delete(plugin.pluginName);
    plugin.nodeAdapters.forEach((nodeAdapter) => {
      if (nodeAdapter.nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
        assert(
          nodeAdapter instanceof ProcessFlowchartTaskSchemaNodeAdapter,
          'Node adapter must be a task schema node adapter'
        );
        this.executionTypeToTaskSchemaNodeAdapters.delete(nodeAdapter.executionType);
      } else {
        this.nodeTypeToNodeAdapters.delete(nodeAdapter.nodeType);
      }
    });
  }

  /**
   * Get a plugin by name.
   *
   * An error is thrown if no plugin is found for the given name.
   *
   * @param pluginName the name of the plugin to get.
   * @returns the plugin.
   */
  getPlugin(pluginName: string): ProcessesPluginBase {
    const plugin = this.pluginNameToPlugin.get(pluginName);
    if (plugin == null) {
      throw new Error(
        `No plugin found for name ${pluginName}. Make sure the plugin is registered with the editor.`
      );
    }
    return plugin;
  }

  /**
   * Check if a plugin is registered.
   *
   * @param pluginName the name of the plugin to check.
   * @returns true if the plugin is registered, false otherwise.
   */
  hasPlugin(pluginName: string): boolean {
    return this.pluginNameToPlugin.has(pluginName);
  }

  /**
   * Get a plugin by flowchart type.
   *
   * An error is thrown if no plugin is found for the given flowchart type.
   *
   * @param flowchartType the flowchart type name of the plugin to get.
   * @returns the plugin.
   */
  getPluginForFlowchartType(flowchartType: string): ProcessesPluginBase {
    const plugin = this.flowchartTypeToPlugin.get(flowchartType);
    if (plugin == null) {
      throw new Error(
        `No plugin found for flowchart type ${flowchartType}. Make sure the plugin is registered with the editor.`
      );
    }
    return plugin;
  }

  /**
   * Get a task schema node adapter by execution type.
   *
   * An error is thrown if no node adapter is found for the given execution type.
   *
   * @param executionType the execution type for tasks of nodes that this adapter can handle.
   * @returns the node adapter.
   */
  getTaskSchemaNodeAdapter(
    executionType: ProcessExecutionType
  ): ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> {
    const nodeAdapter = this.executionTypeToTaskSchemaNodeAdapters.get(executionType);
    if (nodeAdapter == null) {
      throw new Error(
        `No node adapter found for execution type ${executionType}. Make sure the plugin with this adapter is registered with the editor.`
      );
    }
    return nodeAdapter;
  }

  /**
   * Get a node adapter by node type and execution type (for task schema nodes).
   *
   * An error is thrown if no node adapter is found for the given node type / execution type.
   */
  getNodeAdapter(
    nodeType: ProcessFlowchartNodeType,
    executionType?: ProcessExecutionType
  ): ProcessFlowchartNodeAdapter {
    if (nodeType === ProcessFlowchartNodeType.TASK_SCHEMA_NODE) {
      assert(executionType != null, 'Execution type is required for task schema nodes');
      return this.getTaskSchemaNodeAdapter(executionType);
    }
    const nodeAdapter = this.nodeTypeToNodeAdapters.get(nodeType);
    if (nodeAdapter == null) {
      throw new Error(
        `No node adapter found for node type ${nodeType}. Make sure the plugin with this adapter is registered with the editor.`
      );
    }
    return nodeAdapter;
  }
}

/**
 * Singleton instance of registry for use with the editor.
 */
export const PLUGIN_REGISTRY_INSTANCE = new PluginRegistry();

/**
 * Export the registry for testing purposes.
 */
export {PluginRegistry as __PluginRegistryForTesting};
